import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const LanguageDropdown = ({
  isOpen,
  onToggle,
  currentLang,
  currentLangConfig,
  languages,
  changeLang,
  isRTL,
}) => (
  <div className="relative hidden md:block">
    <button
      onClick={onToggle}
      className="flex items-center gap-1 text-sm text-gray-600 hover:text-[#9B4B8C] transition-colors"
      data-testid="lang-dropdown-toggle"
    >
      <span className="text-lg">{currentLangConfig?.flag}</span>
      <ChevronDown className="w-4 h-4" />
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 bg-white rounded-lg shadow-lg py-2 min-w-[140px] z-50 border`}
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLang(lang.code)}
              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${
                currentLang === lang.code ? 'bg-[#9B4B8C]/10 text-[#9B4B8C]' : ''
              }`}
            >
              <span>{lang.flag}</span>
              <span>{lang.name}</span>
            </button>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const DesktopNav = ({ navigation, pathname }) => (
  <nav className="hidden lg:flex items-center space-x-1">
    {navigation.map((item) => {
      const isActive = pathname === item.href;
      return (
        <Link
          key={item.key}
          to={item.href}
          className={`px-4 py-2 text-sm font-medium transition-colors relative group ${
            isActive ? 'text-[#9B4B8C]' : 'text-gray-700 hover:text-[#9B4B8C]'
          }`}
        >
          {item.name}
          <span
            className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-[#9B4B8C] transition-all duration-300 ${
              isActive ? 'w-3/4' : 'w-0 group-hover:w-3/4'
            }`}
          />
        </Link>
      );
    })}
  </nav>
);

const MobileMenu = ({ isOpen, navigation, pathname, languages, currentLang, changeLang }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        className="lg:hidden bg-white border-t shadow-lg overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-4 py-4">
          <nav className="flex flex-col space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.key}
                to={item.href}
                className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  pathname === item.href
                    ? 'bg-[#9B4B8C]/10 text-[#9B4B8C]'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center gap-2 flex-wrap">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLang(lang.code)}
                  className={`px-3 py-2 text-sm rounded-lg transition-colors flex items-center gap-2 ${
                    currentLang === lang.code
                      ? 'bg-[#9B4B8C]/10 text-[#9B4B8C]'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <span>{lang.flag}</span>
                  <span>{lang.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

const SearchBar = ({ isOpen, isRTL, currentLang }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        className="overflow-hidden"
      >
        <div className="py-4">
          <input
            type="text"
            placeholder={currentLang === 'ar' ? 'بحث...' : 'Rechercher...'}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#9B4B8C] focus:border-transparent"
            autoFocus
            dir={isRTL ? 'rtl' : 'ltr'}
          />
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

const useNavItems = (t) => [
  { name: t.nav.home, href: '/', key: 'home' },
  { name: t.nav.about, href: '/about', key: 'about' },
  { name: t.nav.markets, href: '/markets', key: 'markets' },
  { name: t.nav.presence, href: '/presence', key: 'presence' },
  { name: t.nav.careers, href: '/careers', key: 'careers' },
  { name: t.nav.contact, href: '/contact', key: 'contact' },
];

export { DesktopNav, MobileMenu, LanguageDropdown, SearchBar, useNavItems };
